import Phaser from 'phaser';

// Static counter for unique texture keys
let barrierIdCounter = 0;

export class Barrier extends Phaser.Physics.Arcade.Sprite {
  private static readonly GRID_COLS = 12; // 5px per cell in 60px width
  private static readonly GRID_ROWS = 8;  // 5px per cell in 40px height
  private static readonly CELL_SIZE = 5;
  private static readonly WIDTH = 60;
  private static readonly HEIGHT = 40;

  private pixelGrid: boolean[][];
  private textureKey: string;
  private canvasTexture: Phaser.Textures.CanvasTexture | null = null;
  private isDestroyed: boolean = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    // Create unique texture key for this barrier
    const uniqueKey = `barrier_${barrierIdCounter++}`;
    
    // Create the canvas texture first
    const canvas = scene.textures.createCanvas(uniqueKey, Barrier.WIDTH, Barrier.HEIGHT);
    
    super(scene, x, y, uniqueKey);
    
    this.textureKey = uniqueKey;
    this.canvasTexture = canvas;
    
    // Initialize pixel grid - all cells start intact
    this.pixelGrid = [];
    for (let row = 0; row < Barrier.GRID_ROWS; row++) {
      this.pixelGrid[row] = [];
      for (let col = 0; col < Barrier.GRID_COLS; col++) {
        this.pixelGrid[row][col] = true;
      }
    }
    
    // Draw initial barrier texture
    this.redrawTexture();
    
    // Add to scene and enable physics
    scene.add.existing(this);
    scene.physics.add.existing(this);
    
    // Make immovable
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setImmovable(true);
    body.setSize(56, 36);
  }

  /**
   * Take damage at a specific position (relative to barrier center)
   * @param localX - X offset from barrier center
   * @param localY - Y offset from barrier center
   * @returns true if barrier is fully destroyed
   */
  takeDamageAt(localX: number, localY: number): boolean {
    if (this.isDestroyed || !this.active) return true;
    
    // Convert local position to grid coordinates
    let gridX = Math.floor((localX + Barrier.WIDTH / 2) / Barrier.CELL_SIZE);
    let gridY = Math.floor((localY + Barrier.HEIGHT / 2) / Barrier.CELL_SIZE);
    
    // Clamp to valid grid bounds so edge hits still cause damage
    gridX = Math.max(0, Math.min(Barrier.GRID_COLS - 1, gridX));
    gridY = Math.max(0, Math.min(Barrier.GRID_ROWS - 1, gridY));
    
    // Erosion radius (in grid cells)
    const radius = 2;
    let cellsDestroyed = 0;
    const destroyedPositions: { x: number; y: number }[] = [];
    
    // Always destroy the center cell first (if intact)
    if (this.pixelGrid[gridY][gridX]) {
      this.pixelGrid[gridY][gridX] = false;
      cellsDestroyed++;
      const worldX = this.x - Barrier.WIDTH / 2 + gridX * Barrier.CELL_SIZE + Barrier.CELL_SIZE / 2;
      const worldY = this.y - Barrier.HEIGHT / 2 + gridY * Barrier.CELL_SIZE + Barrier.CELL_SIZE / 2;
      destroyedPositions.push({ x: worldX, y: worldY });
    }
    
    // Destroy additional cells in radius around impact point with some randomness
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        // Skip center cell (already handled)
        if (dx === 0 && dy === 0) continue;
        
        const cx = gridX + dx;
        const cy = gridY + dy;
        
        // Check bounds
        if (cx < 0 || cx >= Barrier.GRID_COLS || cy < 0 || cy >= Barrier.GRID_ROWS) {
          continue;
        }
        
        // Check if cell is intact
        if (!this.pixelGrid[cy][cx]) continue;
        
        // Calculate distance from impact
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Higher chance to destroy cells closer to impact
        // Immediate neighbors have high chance, outer cells random
        const destroyChance = dist <= 1.5 ? 0.85 : (radius - dist + 0.5) / radius;
        
        if (Math.random() < destroyChance) {
          this.pixelGrid[cy][cx] = false;
          cellsDestroyed++;
          
          // Store world position for debris particles
          const worldX = this.x - Barrier.WIDTH / 2 + cx * Barrier.CELL_SIZE + Barrier.CELL_SIZE / 2;
          const worldY = this.y - Barrier.HEIGHT / 2 + cy * Barrier.CELL_SIZE + Barrier.CELL_SIZE / 2;
          destroyedPositions.push({ x: worldX, y: worldY });
        }
      }
    }
    
    // Create debris particles
    if (cellsDestroyed > 0) {
      this.createDebrisParticles(destroyedPositions);
    }
    
    // Check if barrier should be destroyed
    const remainingCells = this.countRemainingCells();
    
    if (remainingCells === 0) {
      this.destroyBarrier();
      return true;
    }
    
    // Redraw the texture
    this.redrawTexture();
    
    return false;
  }

  /**
   * Legacy method for backward compatibility
   */
  takeDamage(_amount: number = 1): boolean {
    // Take damage at center if no position specified
    return this.takeDamageAt(0, 0);
  }

  /**
   * Take heavy damage at a specific position (larger erosion radius)
   * Used when enemies crash into the barrier
   */
  takeHeavyDamageAt(localX: number, localY: number): boolean {
    if (this.isDestroyed || !this.active) return true;
    
    // Convert local position to grid coordinates
    let gridX = Math.floor((localX + Barrier.WIDTH / 2) / Barrier.CELL_SIZE);
    let gridY = Math.floor((localY + Barrier.HEIGHT / 2) / Barrier.CELL_SIZE);
    
    // Clamp to valid grid bounds
    gridX = Math.max(0, Math.min(Barrier.GRID_COLS - 1, gridX));
    gridY = Math.max(0, Math.min(Barrier.GRID_ROWS - 1, gridY));
    
    // Larger erosion radius for heavy damage
    const radius = 4;
    let cellsDestroyed = 0;
    const destroyedPositions: { x: number; y: number }[] = [];
    
    // Destroy cells in larger radius with higher probability
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const cx = gridX + dx;
        const cy = gridY + dy;
        
        // Check bounds
        if (cx < 0 || cx >= Barrier.GRID_COLS || cy < 0 || cy >= Barrier.GRID_ROWS) {
          continue;
        }
        
        // Check if cell is intact
        if (!this.pixelGrid[cy][cx]) continue;
        
        // Calculate distance from impact
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Higher destruction chance for heavy damage
        // Inner cells always destroyed, outer cells high chance
        const destroyChance = dist <= 2 ? 1.0 : dist <= 3 ? 0.9 : 0.6;
        
        if (Math.random() < destroyChance) {
          this.pixelGrid[cy][cx] = false;
          cellsDestroyed++;
          
          // Store world position for debris particles
          const worldX = this.x - Barrier.WIDTH / 2 + cx * Barrier.CELL_SIZE + Barrier.CELL_SIZE / 2;
          const worldY = this.y - Barrier.HEIGHT / 2 + cy * Barrier.CELL_SIZE + Barrier.CELL_SIZE / 2;
          destroyedPositions.push({ x: worldX, y: worldY });
        }
      }
    }
    
    // Create debris particles (more for heavy damage)
    if (cellsDestroyed > 0) {
      this.createHeavyDebrisParticles(destroyedPositions);
    }
    
    // Check if barrier should be destroyed
    const remainingCells = this.countRemainingCells();
    
    if (remainingCells === 0) {
      this.destroyBarrier();
      return true;
    }
    
    // Redraw the texture
    this.redrawTexture();
    
    return false;
  }

  private createHeavyDebrisParticles(positions: { x: number; y: number }[]): void {
    // More particles for heavy damage
    const maxParticles = Math.min(positions.length, 12);
    
    for (let i = 0; i < maxParticles; i++) {
      const pos = positions[i];
      
      // Create debris chunks
      const isRed = Math.random() > 0.5;
      const color = isRed ? 0xcc2222 : 0xcccccc;
      const size = 3 + Math.random() * 4;
      
      const debris = this.scene.add.rectangle(pos.x, pos.y, size, size, color);
      
      // More explosive velocity
      const vx = (Math.random() - 0.5) * 100;
      const vy = -30 + Math.random() * 80;
      const rotation = (Math.random() - 0.5) * 6;
      
      this.scene.tweens.add({
        targets: debris,
        x: pos.x + vx,
        y: pos.y + vy,
        rotation: rotation,
        alpha: 0,
        scale: 0.2,
        duration: 500 + Math.random() * 300,
        ease: 'Power2',
        onComplete: () => debris.destroy(),
      });
    }
    
    // Bigger flash at impact
    const centerX = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
    const centerY = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;
    
    const flash = this.scene.add.circle(centerX, centerY, 15, 0xffffff, 1);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 2,
      duration: 150,
      onComplete: () => flash.destroy(),
    });
    
    // Add impact ring
    const ring = this.scene.add.circle(centerX, centerY, 10, 0xffaa00, 0);
    ring.setStrokeStyle(3, 0xffaa00, 1);
    this.scene.tweens.add({
      targets: ring,
      radius: 30,
      alpha: 0,
      duration: 250,
      ease: 'Power2',
      onComplete: () => ring.destroy(),
    });
  }

  private countRemainingCells(): number {
    let count = 0;
    for (let row = 0; row < Barrier.GRID_ROWS; row++) {
      for (let col = 0; col < Barrier.GRID_COLS; col++) {
        if (this.pixelGrid[row][col]) count++;
      }
    }
    return count;
  }

  private redrawTexture(): void {
    if (!this.canvasTexture) return;
    
    const ctx = this.canvasTexture.getContext();
    
    // Clear canvas
    ctx.clearRect(0, 0, Barrier.WIDTH, Barrier.HEIGHT);
    
    // Draw each intact cell
    for (let row = 0; row < Barrier.GRID_ROWS; row++) {
      for (let col = 0; col < Barrier.GRID_COLS; col++) {
        if (!this.pixelGrid[row][col]) continue;
        
        const x = col * Barrier.CELL_SIZE;
        const y = row * Barrier.CELL_SIZE;
        
        // Checkerboard pattern - red and silver
        const isRed = (row + col) % 2 === 0;
        
        if (isRed) {
          ctx.fillStyle = '#c22';
        } else {
          // Silver gradient effect
          const gradientOffset = row / Barrier.GRID_ROWS;
          const brightness = Math.floor(180 + 40 * Math.sin(gradientOffset * Math.PI));
          ctx.fillStyle = `rgb(${brightness}, ${brightness}, ${brightness})`;
        }
        
        ctx.fillRect(x, y, Barrier.CELL_SIZE, Barrier.CELL_SIZE);
        
        // Add subtle border for 3D effect
        ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.fillRect(x + Barrier.CELL_SIZE - 1, y, 1, Barrier.CELL_SIZE);
        ctx.fillRect(x, y + Barrier.CELL_SIZE - 1, Barrier.CELL_SIZE, 1);
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.fillRect(x, y, Barrier.CELL_SIZE, 1);
        ctx.fillRect(x, y, 1, Barrier.CELL_SIZE);
      }
    }
    
    // Chrome trim on top row (if any cells remain there)
    ctx.fillStyle = '#e8e8e8';
    for (let col = 0; col < Barrier.GRID_COLS; col++) {
      if (this.pixelGrid[0][col]) {
        ctx.fillRect(col * Barrier.CELL_SIZE, 0, Barrier.CELL_SIZE, 2);
      }
    }
    
    // Chrome trim on bottom row (if any cells remain there)
    ctx.fillStyle = '#888';
    for (let col = 0; col < Barrier.GRID_COLS; col++) {
      if (this.pixelGrid[Barrier.GRID_ROWS - 1][col]) {
        ctx.fillRect(col * Barrier.CELL_SIZE, Barrier.HEIGHT - 2, Barrier.CELL_SIZE, 2);
      }
    }
    
    this.canvasTexture.refresh();
  }

  private createDebrisParticles(positions: { x: number; y: number }[]): void {
    // Limit particles for performance
    const maxParticles = Math.min(positions.length, 6);
    
    for (let i = 0; i < maxParticles; i++) {
      const pos = positions[i];
      
      // Create small debris chunk
      const isRed = Math.random() > 0.5;
      const color = isRed ? 0xcc2222 : 0xcccccc;
      const size = 2 + Math.random() * 3;
      
      const debris = this.scene.add.rectangle(pos.x, pos.y, size, size, color);
      
      // Random velocity - mostly downward
      const vx = (Math.random() - 0.5) * 60;
      const vy = 30 + Math.random() * 50;
      const rotation = (Math.random() - 0.5) * 4;
      
      this.scene.tweens.add({
        targets: debris,
        x: pos.x + vx,
        y: pos.y + vy,
        rotation: rotation,
        alpha: 0,
        scale: 0.3,
        duration: 400 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => debris.destroy(),
      });
    }
    
    // Small flash at impact
    const centerX = positions.reduce((sum, p) => sum + p.x, 0) / positions.length;
    const centerY = positions.reduce((sum, p) => sum + p.y, 0) / positions.length;
    
    const flash = this.scene.add.circle(centerX, centerY, 8, 0xffffff, 0.8);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 1.5,
      duration: 100,
      onComplete: () => flash.destroy(),
    });
  }

  private destroyBarrier(): void {
    this.isDestroyed = true;
    this.setActive(false);
    this.setVisible(false);
    
    // Disable physics body
    if (this.body) {
      (this.body as Phaser.Physics.Arcade.Body).enable = false;
    }
    
    // Create final destruction effect
    this.createFinalDestructionEffect();
    
    // Destroy after a short delay to avoid collision issues
    this.scene.time.delayedCall(10, () => {
      if (this.scene) {
        // Clean up the texture
        if (this.canvasTexture) {
          this.scene.textures.remove(this.textureKey);
        }
        this.destroy();
      }
    });
  }

  private createFinalDestructionEffect(): void {
    // Scatter remaining debris
    for (let i = 0; i < 8; i++) {
      const offsetX = (Math.random() - 0.5) * Barrier.WIDTH;
      const offsetY = (Math.random() - 0.5) * Barrier.HEIGHT;
      
      const isRed = Math.random() > 0.5;
      const color = isRed ? 0xcc2222 : 0xaaaaaa;
      const size = 3 + Math.random() * 4;
      
      const debris = this.scene.add.rectangle(
        this.x + offsetX,
        this.y + offsetY,
        size,
        size,
        color
      );
      
      const vx = (Math.random() - 0.5) * 100;
      const vy = -20 + Math.random() * 80;
      
      this.scene.tweens.add({
        targets: debris,
        x: this.x + offsetX + vx,
        y: this.y + offsetY + vy,
        rotation: (Math.random() - 0.5) * 6,
        alpha: 0,
        scale: 0.2,
        duration: 500 + Math.random() * 300,
        ease: 'Power2',
        onComplete: () => debris.destroy(),
      });
    }
  }

  getHealth(): number {
    // Return approximate health based on remaining cells
    const remaining = this.countRemainingCells();
    const total = Barrier.GRID_COLS * Barrier.GRID_ROWS;
    return Math.ceil((remaining / total) * 10);
  }

  isFullyDestroyed(): boolean {
    return this.isDestroyed;
  }
}
